import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Info, Link2, RefreshCw } from 'lucide-react';
import {
  amazonOauthApi,
  type AmazonOauthConnection,
} from '@/api/client';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';

export type AmazonAdsRealm = 'EU' | 'NA';

const AMAZON_ADS_REALM_OPTIONS: { value: AmazonAdsRealm; label: string }[] = [
  { value: 'EU', label: 'Europe (India)' },
  { value: 'NA', label: 'North America (US)' },
];

export type AmazonRegionOption = {
  value: string;
  label: string;
  realm: AmazonAdsRealm;
};

/** Connections login labels. India and Europe both use the existing EU authorize host. */
export const CONNECTIONS_AMAZON_REGION_OPTIONS: AmazonRegionOption[] = [
  { value: 'IN', label: 'India (IN)', realm: 'EU' },
  { value: 'EU', label: 'Europe (EU)', realm: 'EU' },
  { value: 'US', label: 'United States (US)', realm: 'NA' },
];

function realmLabel(realm: string | null | undefined): string {
  const code = (realm || 'EU').toUpperCase();
  return AMAZON_ADS_REALM_OPTIONS.find((option) => option.value === code)?.label || code;
}

export const AMAZON_OAUTH_CONNECTIONS_QUERY_KEY = ['amazon-oauth-connections'] as const;

async function startAmazonOAuth(credentialId?: string | null, realm?: AmazonAdsRealm | null) {
  // Open synchronously so popup blockers allow the tab after the async authorize call.
  const popup = window.open('about:blank', '_blank');
  try {
    const res = credentialId
      ? await amazonOauthApi.reconnect(credentialId)
      : await amazonOauthApi.getAuthorizeUrl(null, realm || 'EU');
    if (!res.success || !res.data?.authorize_url) {
      popup?.close();
      throw new Error('Could not start Amazon authorization');
    }
    const url = res.data.authorize_url;
    if (popup && !popup.closed) {
      popup.location.href = url;
    } else {
      window.location.href = url;
    }
  } catch (err) {
    popup?.close();
    throw err;
  }
}

export function useAmazonOauthConnections(enabled = true) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) return;

    const refetchConnections = () => {
      void queryClient.invalidateQueries({ queryKey: AMAZON_OAUTH_CONNECTIONS_QUERY_KEY });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refetchConnections();
    };

    window.addEventListener('focus', refetchConnections);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refetchConnections);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, queryClient]);

  return useQuery({
    queryKey: AMAZON_OAUTH_CONNECTIONS_QUERY_KEY,
    queryFn: async () => {
      const res = await amazonOauthApi.listConnections();
      if (!res.success) throw new Error('Failed to load Amazon connections');
      return res.data;
    },
    enabled,
    refetchInterval: 5 * 60_000,
  });
}

export function useConnectAmazonMutation() {
  return useMutation({
    mutationFn: (realm?: AmazonAdsRealm) => startAmazonOAuth(null, realm || 'EU'),
  });
}

export function useReconnectAmazonMutation() {
  return useMutation({
    mutationFn: (credentialId: string) => startAmazonOAuth(credentialId),
  });
}

function healthNeedsAttention(health: string): boolean {
  return health === 'reauth_required' || health === 'expiring_soon' || health === 'inactive_risk';
}

function healthLabel(health: string): string {
  switch (health) {
    case 'reauth_required':
      return 'Re-authorization required';
    case 'expiring_soon':
      return 'Expiring soon';
    case 'inactive_risk':
      return 'At risk (inactive)';
    case 'active':
      return 'Connected';
    default:
      return health ? health.replace(/_/g, ' ') : 'Connected';
  }
}

function healthBadgeClass(health: string): string {
  switch (health) {
    case 'reauth_required':
      return 'bg-error-subtle text-error-ink';
    case 'expiring_soon':
    case 'inactive_risk':
      return 'bg-warning-subtle text-warning-ink';
    case 'active':
    case '':
      return 'bg-success-subtle text-success-ink';
    default:
      return 'bg-surface-muted text-foreground-muted';
  }
}

function connectionTitle(connection: AmazonOauthConnection): string {
  const name = connection.display_name?.trim();
  if (!name) return 'Amazon Ads';
  if (/amazon/i.test(name)) return name;
  return `Amazon Ads (${name})`;
}

function formatAmazonAccountId(id: string): string {
  const digits = id.replace(/\D/g, '');
  if (digits.length === 12 && digits === id.replace(/\s/g, '').replace(/-/g, '')) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8, 12)}`;
  }
  return id;
}

function accountIdLine(connection: AmazonOauthConnection): string | null {
  const managerId = connection.manager_account_id?.trim();
  if (managerId) return `Account ID: ${formatAmazonAccountId(managerId)}`;
  const profiles = connection.profile_ids?.filter(Boolean) ?? [];
  if (profiles.length === 1) return `Profile ID: ${profiles[0]}`;
  if (profiles.length > 1) {
    return `${profiles.length} profiles`;
  }
  return null;
}

function formatConnectedOn(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AmazonOauthBanner() {
  const { data, isLoading } = useAmazonOauthConnections();
  const reconnect = useReconnectAmazonMutation();
  const connect = useConnectAmazonMutation();
  const qc = useQueryClient();

  if (isLoading || !data) return null;

  const needsAttention = (data.connections ?? []).filter(
    (c) =>
      c.health === 'reauth_required' ||
      c.health === 'expiring_soon' ||
      c.health === 'inactive_risk'
  );
  const alerts = data.alerts ?? [];

  if (needsAttention.length === 0 && alerts.length === 0) return null;

  const primary =
    needsAttention.find((c) => c.health === 'reauth_required') ||
    needsAttention.find((c) => c.health === 'expiring_soon') ||
    needsAttention[0];

  const message =
    alerts[0]?.body ||
    alerts[0]?.title ||
    (primary
      ? primary.health === 'reauth_required'
        ? 'Your Amazon Ads authorization is no longer valid. Reconnect to restore access.'
        : primary.health === 'expiring_soon'
          ? `Amazon authorization for ${primary.display_name || 'your account'} expires soon. Reconnect before access is lost.`
          : 'Amazon may revoke this connection after long inactivity. Use it or reconnect.'
      : null);

  if (!message) return null;

  return (
    <div className="flex items-start gap-3 rounded-xl border border-warning/20 bg-warning-subtle px-4 py-3 text-sm text-warning-ink">
      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-warning-ink" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="font-medium">Amazon Ads authorization</p>
        <p className="mt-0.5 text-warning-ink/90">{message}</p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={reconnect.isPending || connect.isPending}
        onClick={() => {
          if (primary) {
            reconnect.mutate(primary.id, {
              onSettled: () => qc.invalidateQueries({ queryKey: AMAZON_OAUTH_CONNECTIONS_QUERY_KEY }),
            });
          } else {
            connect.mutate('EU');
          }
        }}
      >
        {reconnect.isPending || connect.isPending ? (
          <Spinner className="h-3.5 w-3.5" />
        ) : (
          <>
            <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
            Reconnect
          </>
        )}
      </Button>
    </div>
  );
}

function AmazonEmptyIllustration() {
  return (
    <div className="relative h-[4.5rem] w-[5.5rem] shrink-0" aria-hidden>
      <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
        <div className="flex h-4 items-center gap-1 border-b border-border bg-surface-muted px-2">
          <span className="h-1.5 w-1.5 rounded-full bg-border" />
          <span className="h-1.5 w-1.5 rounded-full bg-border" />
          <span className="h-1.5 w-1.5 rounded-full bg-border" />
        </div>
        <div className="flex flex-1 items-center justify-center text-foreground-muted">
          <svg viewBox="0 0 48 28" className="h-8 w-12" fill="none">
            <text
              x="24"
              y="14"
              textAnchor="middle"
              fill="currentColor"
              fontSize="12"
              fontWeight="700"
              fontFamily="Georgia, serif"
            >
              a
            </text>
            <path
              d="M12 18c6 5 18 5 24 0"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <path
              d="M36 18l2.2 3.2"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
      <div className="absolute -bottom-1.5 -right-1.5 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-surface shadow-sm">
        <Link2 className="h-3.5 w-3.5 text-brand-deep" />
      </div>
    </div>
  );
}

function AmazonAccountMark({ showConnected }: { showConnected: boolean }) {
  return (
    <div className="relative h-12 w-12 shrink-0" aria-hidden>
      <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-surface text-foreground">
        <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none">
          <text
            x="24"
            y="28"
            textAnchor="middle"
            fill="currentColor"
            fontSize="22"
            fontWeight="700"
            fontFamily="Georgia, serif"
          >
            a
          </text>
          <path
            d="M12 34c7 6 17 6 24 0"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M36 34l2.2 3.2"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </div>
      {showConnected ? (
        <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-success text-success-foreground">
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      ) : null}
    </div>
  );
}

function AmazonConnectionCard({
  connection,
  reconnectPending,
  onReconnect,
}: {
  connection: AmazonOauthConnection;
  reconnectPending: boolean;
  onReconnect: () => void;
}) {
  const title = connectionTitle(connection);
  const accountLine = accountIdLine(connection);
  const connectedOn = formatConnectedOn(connection.consent_granted_at);
  const attention = healthNeedsAttention(connection.health);
  const daysLeft =
    connection.days_until_expiry != null && attention
      ? `${connection.days_until_expiry} day${connection.days_until_expiry === 1 ? '' : 's'} left`
      : null;

  return (
    <li className="flex flex-col gap-4 rounded-xl border border-border bg-surface px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex min-w-0 items-start gap-3">
        <AmazonAccountMark showConnected={connection.health === 'active' || !connection.health} />
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground">{title}</p>
          {accountLine || connection.created_by_email ? (
            <p className="mt-0.5 truncate text-xs text-foreground-muted">
              {accountLine}
              {connection.created_by_email
                ? `${accountLine ? ' · ' : ''}${connection.created_by_email}`
                : ''}
            </p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'inline-flex items-center rounded-pill px-2 py-0.5 text-xs font-semibold',
                healthBadgeClass(connection.health)
              )}
            >
              {healthLabel(connection.health)}
            </span>
            {daysLeft ? <span className="text-xs text-foreground-subtle">{daysLeft}</span> : null}
            <span className="text-xs text-foreground-muted">{realmLabel(connection.realm)}</span>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-3 sm:justify-end">
        {connectedOn ? (
          <p className="text-xs text-foreground-muted whitespace-nowrap">Connected on {connectedOn}</p>
        ) : null}
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={reconnectPending}
          onClick={onReconnect}
        >
          {reconnectPending ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <>
              <RefreshCw className="h-3.5 w-3.5" aria-hidden />
              Reconnect
            </>
          )}
        </Button>
      </div>
    </li>
  );
}

export function AmazonConnectionsPanel() {
  const { data, isLoading, isError, refetch } = useAmazonOauthConnections();
  const reconnect = useReconnectAmazonMutation();

  return (
    <Card>
      <div className="p-6 sm:p-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 sm:max-w-xl sm:pr-4">
            <h2 className="text-lg font-semibold text-foreground">Amazon Ads connections</h2>
            <p className="mt-1 text-body-sm text-foreground-muted">
              Connect Amazon advertising accounts. Refresh tokens for campaign management expire after
              365 days of consent — reconnect before they lapse.
            </p>
          </div>
          <ConnectAmazonButton className="h-10 shrink-0 px-4" />
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : isError ? (
            <div className="rounded-xl border border-error/20 bg-error-subtle p-4 text-sm text-error-ink">
              Could not load connections.{' '}
              <button type="button" className="font-medium underline" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          ) : (data?.connections?.length ?? 0) === 0 ? (
            <div className="flex flex-col items-center gap-5 rounded-xl border border-border bg-surface-muted/60 px-6 py-8 text-center sm:flex-row sm:text-left">
              <AmazonEmptyIllustration />
              <div className="min-w-0">
                <p className="font-semibold text-foreground">No Amazon accounts connected yet.</p>
                <p className="mt-1 text-body-sm text-foreground-muted">
                  Connect your Amazon advertising account to get started.
                </p>
              </div>
            </div>
          ) : (
            <>
              <ul className="space-y-3">
                {(data?.connections ?? []).map((c: AmazonOauthConnection) => (
                  <AmazonConnectionCard
                    key={c.id}
                    connection={c}
                    reconnectPending={reconnect.isPending}
                    onReconnect={() => reconnect.mutate(c.id)}
                  />
                ))}
              </ul>
              <div className="mt-4 flex items-start gap-3 rounded-xl bg-brand-subtle px-4 py-3 text-body-sm text-brand-deep">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>
                  Tokens expire after 365 days of consent. Reconnect before they lapse to avoid
                  disruptions.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export function ConnectAmazonButton({
  className,
  showConnectButton = true,
  toolbar = false,
  showRegion = true,
  realm: realmProp,
  onRealmChange,
  regionOptions,
  regionSelection,
  onRegionSelectionChange,
}: {
  className?: string;
  showConnectButton?: boolean;
  /** Header chrome: region selector only, no connect action. */
  toolbar?: boolean;
  showRegion?: boolean;
  /** When set with onRealmChange, region stays shared across multiple buttons. */
  realm?: AmazonAdsRealm;
  onRealmChange?: (realm: AmazonAdsRealm) => void;
  /** Distinct labels that can share one authorize realm, such as India and Europe. */
  regionOptions?: AmazonRegionOption[];
  regionSelection?: string;
  onRegionSelectionChange?: (value: string) => void;
}) {
  const connect = useConnectAmazonMutation();
  const [uncontrolledRealm, setUncontrolledRealm] = useState<AmazonAdsRealm>('EU');
  const [uncontrolledSelection, setUncontrolledSelection] = useState(
    () => regionOptions?.[0]?.value ?? 'EU'
  );
  const realm = realmProp ?? uncontrolledRealm;
  const setRealm = (next: AmazonAdsRealm) => {
    if (realmProp === undefined) setUncontrolledRealm(next);
    onRealmChange?.(next);
  };
  const selection = regionSelection ?? uncontrolledSelection;
  const selectedRegion = regionOptions?.find((option) => option.value === selection) ?? regionOptions?.[0];
  const loginRealm = selectedRegion?.realm ?? realm;
  const setSelection = (next: string) => {
    if (regionSelection === undefined) setUncontrolledSelection(next);
    onRegionSelectionChange?.(next);
    const option = regionOptions?.find((item) => item.value === next);
    if (option) setRealm(option.realm);
  };
  const showButton = showConnectButton && !toolbar;
  const regionVisible = showRegion || toolbar;
  const options = regionOptions ?? AMAZON_ADS_REALM_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
    realm: option.value,
  }));
  return (
    <div className={toolbar ? 'inline-flex items-center' : 'flex flex-wrap items-end gap-2'}>
      {regionVisible ? (
      <label
        className={
          toolbar
            ? 'inline-flex items-center gap-2 text-body-sm text-foreground-muted'
            : 'inline-flex flex-col gap-1 text-xs font-medium text-foreground-muted'
        }
      >
        <span className={toolbar ? 'whitespace-nowrap' : undefined}>{toolbar ? 'Region:' : 'Region'}</span>
        <Select
          aria-label="Amazon Ads region"
          value={regionOptions ? selection : realm}
          onChange={(event) =>
            regionOptions
              ? setSelection(event.target.value)
              : setRealm(event.target.value as AmazonAdsRealm)
          }
          className={
            toolbar
              ? 'h-9 min-w-[10.5rem] bg-surface py-1.5 text-body-sm shadow-sm'
              : 'h-10 min-w-[10.5rem] rounded-lg bg-white py-1.5 text-sm shadow-sm'
          }
          disabled={connect.isPending}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </label>
      ) : null}
      {showButton ? (
        <Button
          type="button"
          className={className}
          disabled={connect.isPending}
          onClick={() => connect.mutate(loginRealm)}
        >
          {connect.isPending ? (
            <>
              <Spinner className="h-3.5 w-3.5 mr-1.5" />
              Redirecting…
            </>
          ) : (
            <>
              <Link2 className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              Connect Amazon
            </>
          )}
        </Button>
      ) : null}
    </div>
  );
}
